import { APIService, type ErrorResponse, type RequestBody } from '../api/apiService.js';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { getStorageItem } from '../utils/storage';

export class TransferService extends APIService {
  /**
   * @desc Get process status
   * @return {Promise<* | undefined>}
   * @param processId
   */
  getProcessStatus(processId: string) {
    return this.request(`/transfer/status/${processId}`, 'GET');
  }

  /**
   * @desc Download
   * @param payload
   * @return {Promise<* | undefined>}
   * @example Payload example
   * {
   *   "filesId": [
   *     0
   *   ]
   * }
   */
  download(payload: RequestBody): Promise<any | undefined> {
    return this.request('/transfer/download', 'POST', payload);
  }

  /**
   * Uploads the given payload to the server using a POST request.
   *
   * @param {RequestBody} payload - The data to be uploaded.
   * @param onUploadProgress
   * @example Payload example
   * {
   *   file: File; // The file to be uploaded.
   *   blockId: string; // The progressive number of the current chunk ( timestamp_progressiveNumber )
   *   isLastChunk: boolean; // Whether this is the last chunk of the file
   *   filename: string; // The name of the file
   *   cufi?: string; // The unique identifier of the file returned by the server after the first chunk that needs to be sent in the following chunks
   *   parentId?: number; // The ID of the parent folder if the file is being uploaded to a folder
   *   onBehalfOf?: number; // The ID of the user on behalf of whom the file is being uploaded if present
   * }
   * @return {Promise<any | undefined>} A promise that resolves with the server response or undefined.
   */
  async upload(
    payload: FormData,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
  ): Promise<any | undefined> {
    const endpoint = '/transfer/upload';
    const url = `${import.meta.env.VITE_APP_API_BASE_URL}${endpoint}`;

    const token = await getStorageItem('localStorage', StorageItemKeyEnum.Token);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('POST', url, true);

      // Imposta l'header Authorization se il token è presente
      if (token) {
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
      }

      // Gestisci l'evento di progresso dell'upload
      if (onUploadProgress && xhr.upload) {
        xhr.upload.onprogress = onUploadProgress;
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Analizza la risposta
          const contentType = xhr.getResponseHeader('Content-Type');
          if (contentType && contentType.indexOf('application/json') !== -1) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              console.error("Errore nell'analisi della risposta JSON", e);
              resolve(xhr.responseText);
            }
          } else {
            resolve(xhr.response);
          }
        } else {
          const error: ErrorResponse = new Error(xhr.statusText) as ErrorResponse;
          error.statusText = xhr.statusText;
          error.status = xhr.status;
          reject(error);
        }
      };

      xhr.onerror = () => {
        const error: ErrorResponse = new Error(xhr.statusText) as ErrorResponse;
        error.statusText = xhr.statusText;
        error.status = xhr.status;
        reject(error);
      };

      // Invia la richiesta con il FormData
      xhr.send(payload);
    });
  }
}
